import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Lock, Mail, Shield, Key, UserPlus, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ManagerLogin = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // login, register, verify2fa
  const [loading, setLoading] = useState(false);
  const [canRegister, setCanRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Login form
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
    pin: '',
    code: ''
  });
  
  // Register form
  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    pin: ''
  });
  
  // 2FA verification
  const [pendingManager, setPendingManager] = useState(null);
  const [qrCode, setQrCode] = useState('');
  const [verifyCode, setVerifyCode] = useState('');

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('manager_token');
    if (token) {
      navigate('/manager/dashboard');
    }
    checkCanRegister();
  }, [navigate]);

  const checkCanRegister = async () => {
    try {
      const response = await axios.get(`${API}/manager/check-exists`);
      setCanRegister(response.data.can_create);
    } catch (error) {
      console.error('Check error:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/manager/login`, loginForm);
      localStorage.setItem('manager_token', response.data.token);
      toast.success('Succesvol ingelogd!');
      navigate('/manager/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Inloggen mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/manager/register`, registerForm);
      setPendingManager({ id: response.data.manager_id });
      setQrCode(response.data.qr_code);
      setMode('verify2fa');
      toast.success('Account aangemaakt! Scan nu de QR code.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registratie mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/manager/verify-2fa`, {
        manager_id: pendingManager.id,
        code: verifyCode
      });
      toast.success('2FA ingeschakeld! Je kunt nu inloggen.');
      setMode('login');
      setPendingManager(null);
      setQrCode('');
      setVerifyCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verificatie mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">
            <span className="text-[#D4AF37]">ZuiverParfum</span>
            <span className="text-white ml-2">Manager</span>
          </h1>
          <p className="text-[#57534E] mt-2">Beheerders Portaal</p>
        </div>

        {/* Login Form */}
        {mode === 'login' && (
          <div className="glass rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#D4AF37]/10 rounded-xl">
                <Shield size={24} className="text-[#D4AF37]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Beheerder Login</h2>
                <p className="text-[#57534E] text-sm">Voer je gegevens in</p>
              </div>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">E-mail</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
                  <input
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                    className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                    placeholder="beheerder@zuiverparfum.nl"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">Wachtwoord</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                    className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg pl-10 pr-10 py-3 text-white focus:border-[#D4AF37] outline-none"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#57534E] hover:text-white"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">PIN Code</label>
                <div className="relative">
                  <Key size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
                  <input
                    type="password"
                    value={loginForm.pin}
                    onChange={(e) => setLoginForm({...loginForm, pin: e.target.value})}
                    className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                    placeholder="****"
                    maxLength={4}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">2FA Code (Authenticator)</label>
                <div className="relative">
                  <Shield size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
                  <input
                    type="text"
                    value={loginForm.code}
                    onChange={(e) => setLoginForm({...loginForm, code: e.target.value})}
                    className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#D4AF37] outline-none tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#B45309] hover:from-[#E5C048] hover:to-[#C46310] text-black font-bold rounded-lg transition-all disabled:opacity-50"
              >
                {loading ? 'Inloggen...' : 'Inloggen'}
              </button>
            </form>

            {canRegister && (
              <div className="mt-6 pt-6 border-t border-[#2C1B18]">
                <button
                  onClick={() => setMode('register')}
                  className="w-full flex items-center justify-center gap-2 py-3 border border-[#D4AF37]/30 hover:border-[#D4AF37] text-[#D4AF37] rounded-lg transition-colors"
                >
                  <UserPlus size={18} />
                  Nieuwe Beheerder Registreren
                </button>
              </div>
            )}

            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/admin')}
                className="text-[#57534E] hover:text-white text-sm transition-colors"
              >
                ← Terug naar Admin Login
              </button>
            </div>
          </div>
        )}

        {/* Register Form */}
        {mode === 'register' && (
          <div className="glass rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-green-500/10 rounded-xl">
                <UserPlus size={24} className="text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Beheerder Registreren</h2>
                <p className="text-[#57534E] text-sm">Maximaal 2 beheerders toegestaan</p>
              </div>
            </div>

            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">Gebruikersnaam</label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
                  <input
                    type="text"
                    value={registerForm.username}
                    onChange={(e) => setRegisterForm({...registerForm, username: e.target.value})}
                    className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                    placeholder="manager1"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">E-mail</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
                  <input
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                    className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                    placeholder="beheerder@email.nl"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">Wachtwoord</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                    className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg pl-10 pr-10 py-3 text-white focus:border-[#D4AF37] outline-none"
                    placeholder="Min. 8 karakters"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#57534E] hover:text-white"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">Master PIN</label>
                <div className="relative">
                  <Key size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
                  <input
                    type="password"
                    value={registerForm.pin}
                    onChange={(e) => setRegisterForm({...registerForm, pin: e.target.value})}
                    className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                    placeholder="****"
                    maxLength={4}
                    required
                  />
                </div>
                <p className="text-[#57534E] text-xs mt-1">Vraag de master PIN aan de admin</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition-all disabled:opacity-50"
              >
                {loading ? 'Registreren...' : 'Account Aanmaken'}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => setMode('login')}
                className="text-[#57534E] hover:text-white text-sm transition-colors"
              >
                ← Terug naar login
              </button>
            </div>
          </div>
        )}

        {/* 2FA Verification */}
        {mode === 'verify2fa' && (
          <div className="glass rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-blue-500/10 rounded-xl">
                <Shield size={24} className="text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">2FA Instellen</h2>
                <p className="text-[#57534E] text-sm">Scan met Microsoft Authenticator</p>
              </div>
            </div>

            {qrCode && (
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-white rounded-xl">
                  <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                </div>
              </div>
            )}

            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">Verificatie Code</label>
                <input
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-3 text-white text-center text-2xl tracking-widest focus:border-[#D4AF37] outline-none"
                  placeholder="000000"
                  maxLength={6}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading || verifyCode.length !== 6}
                className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-lg transition-all disabled:opacity-50"
              >
                {loading ? 'Verifiëren...' : '2FA Activeren'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerLogin;
